import { ObjectTypesService } from './services/ObjectTypesService'
import '@/core/app-shell/sidebar/collapsible-menu/SidebarCollapsableMenu.css'
import { useNavigate } from 'react-router-dom'
import { TypeListItem } from './TypeListItem.tsx'
import { SidebarCollapsableMenu } from 'core/app-shell/sidebar/collapsible-menu/SidebarCollapsableMenu.tsx'
import { localStorageKeys } from '../core/configs/local-storage-keys'
import { Box, CircularProgress, Typography } from '@mui/material'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import { useDiRef } from '../common/react/di-hooks'
import { useBehaviorObservable } from '../common/react/observable-hooks'
import { useOptionsPopover } from '../core/popover/popover-hooks.tsx'
import { DefaultPopover } from '../core/popover/DefaultPopover.tsx'
import { TypeOptionsPopoverItems, type TypeOptionPopoverItem } from './TypeOptionsPopoverItems.tsx'
import { useContext } from 'react'
import { useTutorialTarget } from '@/tutorial/TutorialContext'
import { ModalContext } from '../core/modal/ModalContext.tsx'
import { DeleteTypeConfirmModal } from './DeleteTypeConfirmModal.tsx'
import { useRightPanel } from '../core/right-panel/RightPanelContext.tsx'
import { EditTypePanelPage } from './edit-type-panel/EditTypePanelPage.tsx'

interface TypesExplorerProps {
  spaceId: number
}

export function ObjectTypesExplorer({ spaceId }: TypesExplorerProps) {
  const objectTypesService = useDiRef<ObjectTypesService>('ObjectTypesService')
  const types = useBehaviorObservable(objectTypesService.selectedSpaceTypes$)
  const navigate = useNavigate()
  const nodeOptionsPopoverHooks = useOptionsPopover<TypeOptionPopoverItem>()
  const { openModal } = useContext(ModalContext)!
  const { openPanel } = useRightPanel()
  const tutorialTargetRef = useTutorialTarget('types-explorer')

  function onNewTypeClicked() {
    void navigate(`/newType?spaceId=${spaceId}`)
  }

  function onRenameSubmit(typeId: number, name: string) {
    void objectTypesService.renameType(typeId, name)
  }

  function onTypeClicked(typeId: number) {
    openPanel(<EditTypePanelPage key={typeId} typeId={typeId} />)
  }

  function onDeleteRequested(typeId: number) {
    const type = objectTypesService.getTypeFromSelectedSpace(typeId)
    if (!type) return
    void openModal<boolean>((modalProps) => (
      <DeleteTypeConfirmModal {...modalProps} typeName={type.name} />
    )).then((confirmed) => {
      if (confirmed) {
        void objectTypesService.deleteType(typeId)
      }
    })
  }

  return (
    <>
      <div ref={tutorialTargetRef}>
        <SidebarCollapsableMenu
          title={'Types'}
          newButtonText={'+ New Type'}
          onNewButtonClicked={onNewTypeClicked}
          isOpenLocalStorageKey={localStorageKeys.TypeExplorerOpen}
        >
          {!types && <CircularProgress />}
          {types &&
            (types.length > 0 ? (
              types.map((type) => (
                <TypeListItem
                  key={type.id}
                  type={type}
                  optionPopoverHooks={nodeOptionsPopoverHooks}
                  onRenameSubmit={onRenameSubmit}
                  onDeleteRequested={onDeleteRequested}
                  onTypeClicked={onTypeClicked}
                />
              ))
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                <Typography variant="body1">Create a new type</Typography>
                <ArrowUpwardIcon />
              </Box>
            ))}
        </SidebarCollapsableMenu>
      </div>
      <DefaultPopover
        anchorEl={nodeOptionsPopoverHooks.anchorEl}
        close={nodeOptionsPopoverHooks.closePopover}
      >
        <TypeOptionsPopoverItems onItemSelected={nodeOptionsPopoverHooks.setResult} />
      </DefaultPopover>
    </>
  )
}
